// Property-side account types (backend/common/client_types.py is the same
// list). They share the /developer workspace and differ in words and in
// what they book.
export const PROPERTY_KINDS = ['builder', 'reit_leasing', 'investor', 'commercial', 'property_manager'];
export const BOOK_PROPERTY_URL = 'https://www.brxs.ca/book/property';
const BOOK_SITE = 'https://www.brxs.ca/book/site';
// Signed-in property accounts book inside the app (BookVisitDialog ->
// POST /dev/projects/{id}/book), which is the path that applies the REIT
// portfolio discount and files the visit under the property. The www form
// (book.www) is only used before they have a property.
export const IN_APP_BOOK = '/developer/book';

const COPY = {
  builder: {
    label: 'Builder', things: 'Projects', thing: 'project', units: 'Lots', unit: 'lot', namePlaceholder: 'Project name, like Ashfield Meadows',
    subtitle: 'Maps, 3D models, scans and notes for every site.',
    book: { label: 'Book a site visit', href: BOOK_SITE, www: BOOK_SITE },
    siteProgress: false,
    empty: 'No projects yet. Book a site visit and the site appears here, or create one in Projects.',
    deliveries: 'Delivered maps, models, scans and reports land here.',
    onboardTitle: 'Your builder hub',
    onboardBody: 'Book a site visit, and every capture lands under the project with maps, models, reports and comparisons.',
  },
  reit_leasing: {
    label: 'REIT', things: 'Buildings', thing: 'building', units: 'Units', unit: 'unit', namePlaceholder: 'Building name, like 200 King West',
    subtitle: 'Every building, unit and visit in one dated record.',
    book: { label: 'Book a visit', href: IN_APP_BOOK, www: `${BOOK_PROPERTY_URL}?for=reit_leasing` },
    siteProgress: true,
    empty: 'No buildings yet. Add one in Buildings, or book a visit and we set it up for you.',
    deliveries: 'Leasing sets, condition reports and site visits land here.',
    onboardTitle: 'Your portfolio hub',
    onboardBody: 'Leasing media, unit turnovers, acquisition captures and site progress for every building, with portfolio pricing as you grow.',
  },
  investor: {
    label: 'Investor', things: 'Properties', thing: 'property', units: 'Units', unit: 'unit', namePlaceholder: 'Property name, like 45 Main Street',
    subtitle: 'Acquisition captures, portfolio checks and progress on every deal.',
    book: { label: 'Book a capture', href: IN_APP_BOOK, www: `${BOOK_PROPERTY_URL}?for=investor` },
    siteProgress: true,
    empty: 'No properties yet. Add one in Properties, or book a capture and we set it up for you.',
    deliveries: 'Baseline reports, portfolio visits and progress reports land here.',
    onboardTitle: 'Your portfolio hub',
    onboardBody: 'A dated baseline when you buy, a check every quarter after, and progress reports on anything under construction.',
  },
  commercial: {
    label: 'Commercial', things: 'Properties', thing: 'property', units: 'Spaces', unit: 'space', namePlaceholder: 'Property name, like Bayview Business Park',
    subtitle: 'Leasing media, Virtual Visits and site progress for every property.',
    book: { label: 'Book leasing media', href: IN_APP_BOOK, www: `${BOOK_PROPERTY_URL}?for=commercial` },
    siteProgress: true,
    empty: 'No properties yet. Add one in Properties, or book leasing media and we set it up for you.',
    deliveries: 'Photos, floor plans, Virtual Visits and aerials land here.',
    onboardTitle: 'Your leasing hub',
    onboardBody: 'Photos, measured floor plans and a Virtual Visit for every space, aerials for the site, and progress on anything you build.',
  },
  property_manager: {
    label: 'Property manager', things: 'Buildings', thing: 'building', units: 'Units', unit: 'unit', namePlaceholder: 'Building name, like 88 Queen Street',
    subtitle: 'Listing sets, condition reports and turnovers for every unit.',
    book: { label: 'Book a turnover', href: IN_APP_BOOK, www: `${BOOK_PROPERTY_URL}?for=property_manager` },
    siteProgress: false,
    empty: 'No buildings yet. Add one in Buildings, or book a turnover and we set it up for you.',
    deliveries: 'Listing photos, floor plans and condition reports land here.',
    onboardTitle: 'Your rentals hub',
    onboardBody: 'Listing photos and floor plans when a unit turns over, and a dated condition report at every move-in and move-out.',
  },
};

export const isPropertyKind = (k) => PROPERTY_KINDS.includes(k);
export const propertyCopy = (k) => COPY[k] || COPY.builder;

// Where a signed-in account's "Book" goes. Builders keep the www site flow
// (it dispatches and renews). Other property accounts: no property yet goes
// to the www form (it sets the property up); one property opens its booking
// dialog; several open the list to pick one.
export function bookTarget(kind, projects) {
  const c = propertyCopy(kind);
  const guest = { href: c.book.www || c.book.href, external: true };
  if (!isPropertyKind(kind) || kind === 'builder') return guest;
  const list = (Array.isArray(projects) ? projects : []).filter((x) => x && x.id);
  if (list.length === 0) return guest;
  if (list.length === 1) return { href: `/developer/property/${encodeURIComponent(list[0].id)}?book=1`, external: false };
  return { href: '/developer/projects?pick=book', external: false };
}
